import { Stepper as MantineStepper, StepperProps, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { getContrastingTextColor } from '../../../utils/color';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedStepperProps extends StepperProps {
  gradientVariant?: GradientVariant;
}

interface StepperComponent extends React.ForwardRefExoticComponent<ThemedStepperProps & React.RefAttributes<HTMLDivElement>> {
  Step: typeof MantineStepper.Step;
  Completed: typeof MantineStepper.Completed;
}

const Stepper = forwardRef<HTMLDivElement, ThemedStepperProps>(
  ({ gradientVariant = 'primary', styles, children, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];

    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    return (
      <MantineStepper
        ref={ref}
        styles={{
          ...styles,
          stepIcon: {
            '&[data-progress]': {
              backgroundImage: gradientBg,
              borderColor: gradient.from,
              color: autoTextColor,
            },
            '&[data-completed]': {
              backgroundImage: gradientBg,
              borderColor: gradient.from,
              color: autoTextColor,
            },
            ...(styles as any)?.stepIcon,
          },
          separator: {
            '&[data-active]': {
              backgroundImage: `linear-gradient(90deg, ${gradient.from}, ${gradient.to})`,
            },
            ...(styles as any)?.separator,
          },
        }}
        {...rest}
      >
        {children}
      </MantineStepper>
    );
  }
) as StepperComponent;

Stepper.displayName = 'Stepper';

// Attach subcomponents
Stepper.Step = MantineStepper.Step;
Stepper.Completed = MantineStepper.Completed;

export default Stepper;
